// /parent/conferences — browse open slots and manage existing bookings.
// Mobile-first: agenda-style list grouped by day.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarBlank,
  CheckCircle,
  Clock,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listChildrenForParent } from "@/lib/dailyReports";
import {
  bookSlot,
  cancelBooking,
  formatSlotRange,
  listMyFamilyBookings,
  listOpenSlotsForClassroom,
} from "@/lib/conferences";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ChildAvatar from "@/components/children/ChildAvatar";

export default function ParentConferences() {
  const { profile } = useAuth();
  const [children, setChildren] = useState([]);
  const [openSlots, setOpenSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState({ open: false, slot: null });

  const refresh = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const kids = await listChildrenForParent(profile.id);
      setChildren(kids || []);
      let allBookings = [];
      const familyId = kids?.[0]?.family_id;
      if (familyId) {
        allBookings = await listMyFamilyBookings({ familyId });
        setBookings(allBookings);
      }
      // Open slots aggregated across child classrooms.
      const classroomIds = [...new Set((kids || []).map((c) => c.classroom_id).filter(Boolean))];
      const slots = (
        await Promise.all(
          classroomIds.map((cid) => listOpenSlotsForClassroom(cid)),
        )
      ).flat();
      // Hide slots already claimed by THIS family. Read from the freshly
      // fetched list so we don't trail React state by one render.
      const myBookedSlotIds = new Set(
        allBookings.filter((b) => b.status === "booked").map((b) => b.slot_id),
      );
      setOpenSlots(slots.filter((s) => !myBookedSlotIds.has(s.id)));
    } catch (err) {
      toast.error(err.message || "Could not load conferences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const upcomingBookings = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "booked" && b.slot && new Date(b.slot.start_at) > new Date())
        .sort((a, b) => new Date(a.slot.start_at) - new Date(b.slot.start_at)),
    [bookings],
  );

  const onCancelBooking = async (b) => {
    if (!window.confirm(`Cancel your conference for ${b.child?.first_name || "this child"}?`)) return;
    try {
      await cancelBooking(b.id);
      toast.success("Conference cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  return (
    <div data-testid="parent-conferences" className="space-y-5">
      <div>
        <Link
          to="/parent"
          data-testid="parent-conferences-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" />
          Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Conferences
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Book a parent-teacher conference for your child. Pick from
          your teacher's open slots — confirmation is instant.
        </p>
      </div>

      {/* Upcoming bookings */}
      <section>
        <h2 className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
          Your upcoming conferences
        </h2>
        {upcomingBookings.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            None booked yet.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="parent-conferences-bookings">
            {upcomingBookings.map((b) => (
              <li
                key={b.id}
                data-testid={`parent-conference-booking-${b.id}`}
                className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary-hover">
                  <CalendarBlank size={16} weight="duotone" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">
                    {b.child?.first_name} {b.child?.last_name || ""} ·{" "}
                    {b.slot?.teacher?.full_name || "Teacher"}
                  </p>
                  <p className="mt-0.5 text-[12px] text-neutral-700">
                    {formatSlotRange(b.slot?.start_at, b.slot?.end_at)}
                    {b.slot?.location ? ` · ${b.slot.location}` : ""}
                  </p>
                  {b.notes && <p className="mt-0.5 text-[11px] italic text-neutral-500">"{b.notes}"</p>}
                </div>
                <button
                  type="button"
                  data-testid={`parent-conference-cancel-${b.id}`}
                  onClick={() => onCancelBooking(b)}
                  className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Open slots grouped by day */}
      <section>
        <h2 className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
          Available slots
        </h2>
        {loading && (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            Loading…
          </p>
        )}
        {!loading && openSlots.length === 0 && (
          <p
            data-testid="parent-conferences-no-slots"
            className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500"
          >
            Your teacher hasn't published any conference slots yet.
          </p>
        )}
        {!loading && openSlots.length > 0 && (
          <ul className="space-y-2" data-testid="parent-conferences-open-slots">
            {openSlots.map((s) => (
              <li
                key={s.id}
                data-testid={`parent-conference-slot-${s.id}`}
                className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
              >
                <Clock size={16} weight="duotone" className="text-neutral-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">
                    {formatSlotRange(s.start_at, s.end_at)}
                  </p>
                  <p className="text-[11px] text-neutral-500">
                    {s.teacher?.full_name || "Teacher"}
                    {s.classroom?.name ? ` · ${s.classroom.name}` : ""}
                    {s.location ? ` · ${s.location}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`parent-conference-book-${s.id}`}
                  onClick={() => setBook({ open: true, slot: s })}
                  className="rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-primary-hover"
                >
                  Book
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <BookSlotSheet
        open={book.open}
        onOpenChange={(o) => setBook((s) => ({ ...s, open: o }))}
        slot={book.slot}
        children={children}
        parentProfileId={profile?.id}
        onSaved={() => refresh()}
      />
    </div>
  );
}

function BookSlotSheet({ open, onOpenChange, slot, children, parentProfileId, onSaved }) {
  const eligible = useMemo(
    () => (children || []).filter((c) => c.classroom_id === slot?.classroom_id),
    [children, slot?.classroom_id],
  );
  const [childId, setChildId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChildId(eligible[0]?.id || "");
    setNotes("");
  }, [open, eligible]);

  if (!slot) return null;

  const onConfirm = async () => {
    const child = eligible.find((c) => c.id === childId);
    if (!child) {
      toast.error("Pick a child");
      return;
    }
    setBusy(true);
    try {
      await bookSlot({
        organizationId: child.organization_id,
        slotId: slot.id,
        childId: child.id,
        familyId: child.family_id,
        parentProfileId,
        notes,
      });
      toast.success("Conference booked");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      if (err?.code === "23505") {
        toast.error("Someone just booked that slot. Pick another.");
        onSaved?.();
        onOpenChange?.(false);
      } else {
        toast.error(err.message || "Could not book");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="parent-conference-book-sheet"
        side="bottom"
        className="rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">Book this slot</SheetTitle>
          <SheetDescription>
            {formatSlotRange(slot.start_at, slot.end_at)}
            {slot.location ? ` · ${slot.location}` : ""}
            {slot.teacher?.full_name ? ` · with ${slot.teacher.full_name}` : ""}
          </SheetDescription>
        </SheetHeader>

        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Child
          </span>
          <select
            data-testid="parent-conference-child"
            value={childId}
            onChange={(e) => setChildId(e.target.value)}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {eligible.length === 0 && <option value="">No eligible child</option>}
            {eligible.map((c) => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.last_name || ""}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Notes (optional)
          </span>
          <textarea
            rows={2}
            data-testid="parent-conference-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="anything you'd like the teacher to prepare for"
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="parent-conference-book-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="parent-conference-book-confirm"
            disabled={busy || eligible.length === 0}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <CheckCircle size={12} weight="duotone" />
            {busy ? "Booking…" : "Confirm"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
